import {
  LINE_COLORS,
  parseSpeedKmh,
  pointStatusForPlannedSpeed,
} from "../FieldApp/fieldAppTrackingUtils";
import { activeTrailChip } from "./dashboardTrailPeople";

export const MAX_TRACE_PERIOD_MS = 24 * 60 * 60 * 1000;
export const MAX_FENCE_TRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;
export const DEFAULT_TRACE_HOURS = 12;
export const DEFAULT_OPERATOR_TRACE_HOURS = 24;
export const OPERATOR_ONLINE_MS = 2 * 60 * 1000;

export function getTracePeriodForHours(hours, now = new Date()) {
  const end = new Date(now);
  const start = new Date(end.getTime() - Number(hours) * 60 * 60 * 1000);
  return { start, end };
}

export function getDefaultTracePeriod(now = new Date()) {
  return getTracePeriodForHours(DEFAULT_TRACE_HOURS, now);
}

/** 1h e 12h acompanham o relógio. Um período fechado no passado fica onde está. */
export function openTracePeriod(start, end, now = new Date()) {
  const chip = activeTrailChip(start, end, now);
  if (chip === "1h" || chip === "12h") {
    return {
      ...getTracePeriodForHours(chip === "1h" ? 1 : 12, now),
      followsNow: true,
    };
  }
  return { start, end, followsNow: false };
}

export function validateTracePeriod(start, end, options = {}) {
  const maxMs =
    options && Number.isFinite(options.maxMs) ? options.maxMs : MAX_TRACE_PERIOD_MS;
  if (!start || !end) {
    return "Selecione início e fim do período.";
  }
  if (end <= start) {
    return "O fim deve ser posterior ao início.";
  }
  if (end.getTime() - start.getTime() > maxMs) {
    if (maxMs > MAX_TRACE_PERIOD_MS) {
      return "O período máximo com talhões selecionados é de 7 dias.";
    }
    return "O período máximo é de 24 horas.";
  }
  return null;
}

function ringContains(ring, point) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  const x = Number(point.lng);
  const y = Number(point.lat);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pointInsideLandplots(point, landplots) {
  if (!point) return false;
  if (!landplots || !landplots.length) return true;
  return landplots.some((plot) => {
    let geometry = plot && plot.geomap;
    if (!geometry) return false;
    if (typeof geometry === "string") {
      try {
        geometry = JSON.parse(geometry);
      } catch (error) {
        return false;
      }
    }
    const rings =
      geometry.type === "Polygon" && Array.isArray(geometry.coordinates)
        ? geometry.coordinates
        : null;
    if (!rings || !rings.length || !ringContains(rings[0], point)) return false;
    for (let hole = 1; hole < rings.length; hole += 1) {
      if (ringContains(rings[hole], point)) return false;
    }
    return true;
  });
}

export function formatTrackSpeed(speed) {
  if (speed == null || speed === "") return "";
  if (typeof speed === "string") return speed;
  const value = Number(speed);
  if (!Number.isFinite(value)) return "";
  return `${value.toFixed(1)} km/h`;
}

export function trackPointFields(point) {
  if (!point || point.lat == null || point.lng == null) return null;
  const next = { lat: point.lat, lng: point.lng };
  if (point.time) next.time = point.time;
  const speed = formatTrackSpeed(point.speed);
  if (speed) next.speed = speed;
  if (point.course != null && point.course !== "" && Number.isFinite(Number(point.course))) {
    next.course = Number(point.course);
  }
  return next;
}

export function flattenPathReportItem(item) {
  const points = [];
  const path = item && item.c && item.c.path;

  if (!Array.isArray(path)) {
    return points;
  }

  for (const segment of path) {
    if (!Array.isArray(segment)) {
      continue;
    }
    for (const point of segment) {
      const next = trackPointFields(point);
      if (next) points.push(next);
    }
  }

  return points;
}

export function flattenPathReportToTraces(reportData, equipmentById = {}) {
  const traces = {};
  const identToId = {};

  Object.values(equipmentById).forEach((equipment) => {
    if (equipment && equipment.ident != null && equipment.id != null) {
      identToId[equipment.ident] = equipment.id;
    }
  });

  if (!Array.isArray(reportData)) {
    return traces;
  }

  for (const item of reportData) {
    if (!item || item.ident == null) {
      continue;
    }

    const equipmentId = identToId[item.ident];
    if (equipmentId == null) {
      continue;
    }

    traces[equipmentId] = flattenPathReportItem(item);
  }

  return traces;
}

export function countTracePoints(equipment) {
  return Object.values(equipment || {}).reduce((total, item) => {
    const points = item.last_posi_point;
    return total + (Array.isArray(points) ? points.length : 0);
  }, 0);
}

export function sameMapPoint(a, b) {
  if (!a || !b) return false;
  return Number(a.lat) === Number(b.lat) && Number(a.lng) === Number(b.lng);
}

const EARTH_RADIUS_M = 6371000;

function bearingDegrees(from, to) {
  const lat1 = (Number(from.lat) * Math.PI) / 180;
  const lat2 = (Number(to.lat) * Math.PI) / 180;
  const dLng = ((Number(to.lng) - Number(from.lng)) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

export function nearestTrackPoint(path, latLng) {
  let closest = null;
  let minDistance = Infinity;
  (path || []).forEach((point) => {
    if (!point || point.lat == null || point.lng == null) return;
    const distance = distanceMeters(latLng, point);
    if (distance < minDistance) {
      minDistance = distance;
      closest = point;
    }
  });
  return closest;
}

export function trackPointHeading(path, point) {
  const course = Number(point && point.course);
  if (Number.isFinite(course) && course !== 0) {
    return ((course % 360) + 360) % 360;
  }
  const points = path || [];
  let index = points.indexOf(point);
  if (index < 0 && point) {
    index = points.findIndex(
      (item) =>
        item &&
        ((point.time != null && String(item.time) === String(point.time)) ||
          (item.lat === point.lat && item.lng === point.lng))
    );
  }
  if (index < 0) return 0;
  const from = index > 0 ? points[index - 1] : points[index];
  const to = index < points.length - 1 ? points[index + 1] : points[index];
  if (!from || !to || (Number(from.lat) === Number(to.lat) && Number(from.lng) === Number(to.lng))) {
    return 0;
  }
  return (bearingDegrees(from, to) + 360) % 360;
}

export function distanceMeters(from, to) {
  if (!from || !to) return Infinity;
  const lat1 = (Number(from.lat) * Math.PI) / 180;
  const lat2 = (Number(to.lat) * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((Number(to.lng) - Number(from.lng)) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Pulo grande demais para ligar o rastro em linha reta. O ícone acompanha o ponto novo. */
export const MAX_TRACE_SEGMENT_METERS = 5000;
/** Buraco de tempo no GPS do operador: começa outro segmento, não some o rastro. */
export const OPERATOR_TRAIL_GAP_MS = 20 * 60 * 1000;

export function isTraceTeleport(from, to, maxMeters = MAX_TRACE_SEGMENT_METERS) {
  if (!from || !to) return false;
  return distanceMeters(from, to) > maxMeters;
}

export function appendMovedPoint(points, nextPoint) {
  const list = Array.isArray(points) ? points.slice() : [];
  if (!nextPoint || nextPoint.lat == null || nextPoint.lng == null) {
    return list;
  }
  const next = trackPointFields(nextPoint);
  if (!next) return list;
  if (list.length > 0 && sameMapPoint(list[list.length - 1], next)) {
    return list;
  }
  if (list.length > 0 && isTraceTeleport(list[list.length - 1], next)) {
    return list;
  }
  list.push(next);
  return list;
}

/** Continua o segmento atual. Num pulo, abre outro segmento e não liga os dois. */
export function appendOperatorTrailSegments(segments, nextPoint) {
  const segs = Array.isArray(segments)
    ? segments.map((segment) => (Array.isArray(segment) ? segment.slice() : []))
    : [];
  const next = trackPointFields(nextPoint);
  if (!next) return segs.filter((segment) => segment.length);
  const current = segs.length ? segs[segs.length - 1] : null;
  const last = current && current.length ? current[current.length - 1] : null;
  if (last && sameMapPoint(last, next)) return segs;
  if (!last || isTraceTeleport(last, next)) {
    segs.push([next]);
    return segs;
  }
  current.push(next);
  return segs;
}

export function isOperatorOnline(timestamp, now = new Date()) {
  if (!timestamp) return false;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return false;
  return now.getTime() - parsed.getTime() <= OPERATOR_ONLINE_MS;
}

export function operatorPositionToPoint(position) {
  if (!position) return null;
  const lat = Number(position.latitude != null ? position.latitude : position.lat);
  const lng = Number(position.longitude != null ? position.longitude : position.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  return trackPointFields({
    lat,
    lng,
    time: position.timestamp || position.time,
    speed: position.speed,
    course: position.bearing != null ? position.bearing : position.course,
  });
}

export function groupOperatorTrailSegments(positions) {
  const trails = {};
  const lastTimes = {};
  const list = Array.isArray(positions) ? positions.slice() : [];
  list.sort((a, b) => {
    const ta = new Date(a && a.timestamp).getTime() || 0;
    const tb = new Date(b && b.timestamp).getTime() || 0;
    return ta - tb;
  });

  list.forEach((position) => {
    const operatorId = position && position.operator;
    const point = operatorPositionToPoint(position);
    if (operatorId == null || !point) return;
    if (!trails[operatorId]) {
      trails[operatorId] = [[]];
    }
    const segments = trails[operatorId];
    const current = segments[segments.length - 1];
    const last = current[current.length - 1];
    const currentTime = new Date(position.timestamp).getTime() || 0;
    const lastTime = lastTimes[operatorId];
    if (last && sameMapPoint(last, point)) {
      lastTimes[operatorId] = currentTime;
      return;
    }
    const timeGap =
      lastTime != null && currentTime - lastTime > OPERATOR_TRAIL_GAP_MS;
    if (last && (isTraceTeleport(last, point) || timeGap)) {
      segments.push([point]);
      lastTimes[operatorId] = currentTime;
      return;
    }
    current.push(point);
    lastTimes[operatorId] = currentTime;
  });

  return trails;
}

export function flattenTrailSegments(segments) {
  return (segments || []).reduce((all, segment) => all.concat(segment), []);
}

export function groupOperatorTrailPoints(positions) {
  const trails = {};
  const segmented = groupOperatorTrailSegments(positions);
  Object.keys(segmented).forEach((operatorId) => {
    trails[operatorId] = flattenTrailSegments(segmented[operatorId]);
  });
  return trails;
}

/** Ícone: o GPS mais novo. O rastro é quem quebra no pulo, não a posição. */
export function lastAcceptedGpsPosition(positions) {
  let last = null;
  (positions || []).forEach((position) => {
    const point = operatorPositionToPoint(position);
    if (!point) return;
    if (!last || gpsTimestampMs(position) >= gpsTimestampMs(last)) {
      last = position;
    }
  });
  return last;
}

export function buildOperatorMapState(lastKnownPositions, trailPositions, now = new Date()) {
  const operators = {};
  const acceptedLatest = latestPositionsByOperator(
    (lastKnownPositions || []).concat(trailPositions || [])
  );

  acceptedLatest.forEach((position) => {
    const operatorId = position && position.operator;
    const lastKnown = operatorPositionToPoint(position);
    if (operatorId == null || !lastKnown) return;
    operators[operatorId] = {
      id: operatorId,
      name: position.operator_name || position.operator_ident || String(operatorId),
      speed: position.speed,
      last_connection: position.timestamp,
      online: isOperatorOnline(position.timestamp, now),
      lastKnown,
      last_posi_point: [],
      last_posi_segments: [],
      assumedMachineIdent:
        position.equipmentId || position.equipment_id || "",
    };
  });

  const trailSegments = groupOperatorTrailSegments(trailPositions);
  const nameById = {};
  (trailPositions || []).forEach((position) => {
    const operatorId = position && position.operator;
    if (operatorId == null) return;
    const name = position.operator_name || position.operator_ident;
    if (name) nameById[operatorId] = name;
  });

  Object.keys(trailSegments).forEach((id) => {
    const segments = trailSegments[id];
    const points = flattenTrailSegments(segments);
    const lastPoint = points[points.length - 1];
    const existing = operators[id];
    if (existing) {
      operators[id] = {
        ...existing,
        last_posi_point: points,
        last_posi_segments: segments,
      };
      return;
    }
    if (!lastPoint) return;
    operators[id] = {
      id: Number(id) || id,
      name: nameById[id] || String(id),
      speed: null,
      last_connection: null,
      online: false,
      lastKnown: lastPoint,
      last_posi_point: points,
      last_posi_segments: segments,
      assumedMachineIdent: "",
    };
  });

  return operators;
}

export const OPERATOR_BESIDE_PIXELS = 22;
export const COLOCATED_METERS = 40;

/** GPS clock on the device (UTC). Never use server_time / created_at. */
export function gpsTimestampMs(position) {
  if (!position || position.timestamp == null || position.timestamp === "") {
    return 0;
  }
  const time = new Date(position.timestamp).getTime();
  return Number.isFinite(time) ? time : 0;
}

export function formatGpsUtcDisplay(value, timeZone) {
  const time = gpsTimestampMs({ timestamp: value });
  if (!time) return value == null ? "" : String(value);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };
  if (timeZone) options.timeZone = timeZone;
  return new Date(time).toLocaleString("pt-BR", options);
}

export function latestPositionsByOperator(positions) {
  const grouped = {};
  (positions || []).forEach((position) => {
    const id = position && position.operator;
    if (id == null) return;
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(position);
  });
  return Object.keys(grouped)
    .map((id) => lastAcceptedGpsPosition(grouped[id]))
    .filter(Boolean);
}

export function splitPathByTeleport(points) {
  const coords = (points || []).filter(
    (point) => point && point.lat != null && point.lng != null
  );
  if (coords.length === 0) return [];
  const segments = [[coords[0]]];
  for (let i = 1; i < coords.length; i++) {
    const current = segments[segments.length - 1];
    const last = current[current.length - 1];
    if (isTraceTeleport(last, coords[i])) {
      segments.push([coords[i]]);
    } else {
      current.push(coords[i]);
    }
  }
  return segments;
}

function haversineMeters(a, b) {
  const earthRadius = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(Math.min(1, h)));
}

export function pointsAreCoLocated(a, b, meters = COLOCATED_METERS) {
  if (!a || !b) return false;
  if (sameMapPoint(a, b)) return true;
  return haversineMeters(a, b) <= meters;
}

export function offsetLatLngByPixels(
  position,
  zoom,
  pixelX = OPERATOR_BESIDE_PIXELS,
  pixelY = 0
) {
  if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) {
    return position;
  }
  const safeZoom = Number.isFinite(zoom) ? zoom : 14;
  const latRad = (position.lat * Math.PI) / 180;
  const metersPerPixel =
    (Math.cos(latRad) * 40075016.686) / (256 * Math.pow(2, safeZoom));
  const metersEast = pixelX * metersPerPixel;
  const metersSouth = pixelY * metersPerPixel;
  const dLat = -metersSouth / 110540;
  const cosLat = Math.cos(latRad);
  const dLng = cosLat === 0 ? 0 : metersEast / (111320 * cosLat);
  return {
    lat: position.lat + dLat,
    lng: position.lng + dLng,
  };
}

export function offsetIfCoLocated(position, others, delta = 0.00014) {
  if (!position) return position;
  const conflict = (others || []).some((other) =>
    pointsAreCoLocated(other, position)
  );
  if (!conflict) return position;
  return {
    lat: position.lat + delta,
    lng: position.lng + delta,
  };
}

export function placeOperatorBesideMachines(position, machinePositions, zoom) {
  if (!position) return position;
  const conflict = (machinePositions || []).some((machine) =>
    pointsAreCoLocated(machine, position)
  );
  if (!conflict) return position;
  return offsetLatLngByPixels(position, zoom);
}

export const MARKER_ROW_GAP_PX = 8;

export function metersPerPixel(lat, zoom) {
  const safeZoom = Number.isFinite(zoom) ? zoom : 14;
  const latRad = ((Number(lat) || 0) * Math.PI) / 180;
  return (Math.cos(latRad) * 40075016.686) / (256 * 2 ** safeZoom);
}

function projectFromOrigin(position, origin, zoom) {
  const mpp = metersPerPixel(origin.lat, zoom);
  const latRad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);
  const east =
    (position.lng - origin.lng) * (cosLat === 0 ? 1 : 111320 * cosLat);
  const north = (position.lat - origin.lat) * 110540;
  return { x: east / mpp, y: -north / mpp };
}

/** Faixa de toque com o dobro da linha desenhada. */
export const TRAIL_TOUCH_SCALE = 2;

export function trailTouchRadiusPx(strokeWeight) {
  const visible = Number(strokeWeight) > 0 ? Number(strokeWeight) : 4;
  return (visible * TRAIL_TOUCH_SCALE) / 2;
}

function distanceToSegmentPx(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const raw = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq;
  const t = Math.max(0, Math.min(1, raw));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

export function nearestTrailAtClick(click, trails, zoom) {
  if (
    !click ||
    !Number.isFinite(Number(click.lat)) ||
    !Number.isFinite(Number(click.lng))
  ) {
    return null;
  }
  let best = null;
  let bestDistance = Infinity;
  (trails || []).forEach((trail) => {
    const path = trail && trail.path;
    if (!Array.isArray(path) || path.length < 2) return;
    const radius = trailTouchRadiusPx(trail.strokeWeight);
    let distance = Infinity;
    for (let index = 0; index < path.length - 1; index += 1) {
      const start = path[index];
      const end = path[index + 1];
      if (
        !start ||
        !end ||
        start.lat == null ||
        start.lng == null ||
        end.lat == null ||
        end.lng == null
      ) {
        continue;
      }
      const segment = distanceToSegmentPx(
        { x: 0, y: 0 },
        projectFromOrigin(start, click, zoom),
        projectFromOrigin(end, click, zoom)
      );
      if (segment < distance) distance = segment;
    }
    if (distance <= radius && distance < bestDistance) {
      bestDistance = distance;
      best = trail;
    }
  });
  return best;
}

function positionFromPixel(pixel, origin, zoom) {
  const mpp = metersPerPixel(origin.lat, zoom);
  const latRad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);
  const east = pixel.x * mpp;
  const north = -pixel.y * mpp;
  return {
    lat: origin.lat + north / 110540,
    lng: origin.lng + (cosLat === 0 ? 0 : east / (111320 * cosLat)),
  };
}

function iconBoxesOverlap(a, b, gap) {
  const pad = gap / 2;
  return (
    a.x - a.width / 2 - pad < b.x + b.width / 2 &&
    a.x + a.width / 2 + pad > b.x - b.width / 2 &&
    a.y - a.height - pad < b.y &&
    a.y + pad > b.y - b.height
  );
}

function boundsOf(items) {
  return items.reduce(
    (box, item) => ({
      left: Math.min(box.left, item.x - item.width / 2),
      right: Math.max(box.right, item.x + item.width / 2),
    }),
    { left: Infinity, right: -Infinity }
  );
}

/**
 * Operador e a própria máquina ficam a 22 px. Outro ícone que cair em cima
 * é empurrado para o lado, e no zoom volta ao GPS quando já cabe na tela.
 */
export function spreadMarkersSideBySide(
  markers,
  zoom,
  gap = MARKER_ROW_GAP_PX
) {
  const list = (markers || []).map((marker, index) => {
    const position = marker && marker.position;
    const beside =
      marker &&
      marker.stickBeside &&
      position &&
      Number.isFinite(position.lat) &&
      Number.isFinite(position.lng)
        ? offsetLatLngByPixels(position, zoom, OPERATOR_BESIDE_PIXELS, 0)
        : position;
    return { marker, index, position: beside };
  });
  const placed = list.map((item) => item.position);
  const visible = list.filter(
    (item) =>
      item.position &&
      Number.isFinite(item.position.lat) &&
      Number.isFinite(item.position.lng)
  );
  if (visible.length < 2) return placed;

  const origin = visible[0].position;
  const pixels = visible.map((item) => {
    const point = projectFromOrigin(item.position, origin, zoom);
    return {
      ...item,
      ...point,
      width: Number(item.marker.width) || 48,
      height: Number(item.marker.height) || 40,
    };
  });

  const parent = pixels.map((_, index) => index);
  const find = (index) => {
    let root = index;
    while (parent[root] !== root) root = parent[root];
    while (parent[index] !== root) {
      const next = parent[index];
      parent[index] = root;
      index = next;
    }
    return root;
  };

  for (let i = 0; i < pixels.length; i += 1) {
    for (let j = i + 1; j < pixels.length; j += 1) {
      if (iconBoxesOverlap(pixels[i], pixels[j], gap)) {
        parent[find(i)] = find(j);
      }
    }
  }

  const groups = new Map();
  pixels.forEach((item, index) => {
    const root = find(index);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(item);
  });

  groups.forEach((group) => {
    if (group.length < 2) return;
    const units = [];
    const used = new Set();
    group.forEach((item) => {
      if (used.has(item)) return;
      const pairId = item.marker && item.marker.pairId;
      const partner =
        pairId &&
        group.find(
          (other) =>
            other !== item && other.marker && other.marker.pairId === pairId
        );
      const members = partner ? [item, partner] : [item];
      members.forEach((member) => used.add(member));
      const box = boundsOf(members);
      units.push({ members, left: box.left, right: box.right });
    });
    if (units.length < 2) return;

    units.sort((a, b) => a.left - b.left || a.members[0].index - b.members[0].index);
    let edge = -Infinity;
    units.forEach((unit) => {
      if (unit.left < edge + gap) {
        const shift = edge + gap - unit.left;
        unit.members.forEach((member) => {
          member.x += shift;
        });
        unit.left += shift;
        unit.right += shift;
      }
      edge = Math.max(edge, unit.right);
    });

    units.forEach((unit) => {
      unit.members.forEach((member) => {
        placed[member.index] = positionFromPixel(
          { x: member.x, y: member.y },
          origin,
          zoom
        );
      });
    });
  });

  return placed;
}

function speedColor(point, plannedSpeedKmh) {
  const status = pointStatusForPlannedSpeed(
    parseSpeedKmh(point && point.speed),
    plannedSpeedKmh
  );
  return LINE_COLORS[status] || LINE_COLORS.OK;
}

/** Quebra o rastro na mesma escala da operação: amarelo, azul ou vermelho. */
export function splitPathBySpeedColor(points, plannedSpeedKmh) {
  const list = (points || []).filter(
    (point) => point && point.lat != null && point.lng != null
  );
  if (list.length < 2) return [];

  const planned = Number(plannedSpeedKmh);
  const reference = Number.isFinite(planned) && planned > 0 ? planned : 15;
  const segments = [];
  let current = null;

  for (let index = 1; index < list.length; index += 1) {
    const color = speedColor(list[index], reference);
    if (!current || current.color !== color) {
      current = { color, path: [list[index - 1], list[index]] };
      segments.push(current);
    } else {
      current.path.push(list[index]);
    }
  }

  return segments;
}
